/**
 * timeService.js
 * Centralized time synchronization service for display devices.
 * Provides server-adjusted time via drift compensation.
 *
 * ARD-26: Master Clock Integration
 *
 * Accuracy & reliability model:
 * - Offset is estimated NTP-style: offset = serverNow - (t0 + t3) / 2, where
 *   t0 is the client send time and t3 is the client receive time. This removes
 *   the systematic bias introduced by request/response latency.
 * - Samples whose round-trip time exceeds MAX_RTT_MS are discarded (the last
 *   known good offset is retained) to avoid corrupting the clock with a laggy
 *   or GC-stalled response.
 * - A monotonic clock (performance.now) anchors ticking between resyncs so a
 *   wall-clock jump (NTP step, manual change, DST on a misconfigured host) does
 *   not corrupt the displayed time.
 * - A single large jump in the measured offset is treated as suspect and is not
 *   adopted until it is confirmed by a second consecutive sample, guarding
 *   against one-off outliers while still following genuine step changes.
 */

import axios from "axios";
const serverUrl = import.meta.env.VITE_SERVER_BASE_URL;

// Reject samples slower than this round-trip (ms). A high RTT means the midpoint
// estimate is too uncertain to trust.
const MAX_RTT_MS = 1500;

// If a new offset differs from the current one by more than this (ms), treat it
// as suspect and require a second consecutive confirming sample before adopting.
const SUSPECT_JUMP_MS = 2000;

// Default drift tolerance for monitoring; overridden by server-provided value.
const DEFAULT_DRIFT_TOLERANCE_MS = 1000;

let drift = 0; // serverTime - clientTime (ms)
let lastSyncAt = 0;
let syncFailed = false;
let lastRtt = Infinity;
let hasSynced = false;
let driftToleranceMs = DEFAULT_DRIFT_TOLERANCE_MS;

// Pending suspect offset awaiting confirmation (null when none).
let pendingOffset = null;

// Monotonic anchor: the performance.now() value captured at the last accepted
// sync, and the corresponding corrected wall-clock time. Used so ticks advance
// via a monotonic source rather than raw Date.now() between resyncs.
const hasPerfNow = typeof performance !== "undefined" && typeof performance.now === "function";
let monoAnchor = hasPerfNow ? performance.now() : 0;
let wallAnchorAtSync = Date.now();

/**
 * Monotonic "now" in ms. Falls back to Date.now() where performance.now is
 * unavailable.
 * @returns {number}
 */
function monoNow() {
  return hasPerfNow ? performance.now() : Date.now();
}

/**
 * Apply a freshly measured server offset, with outlier confirmation and drift
 * monitoring. Re-anchors the monotonic base.
 * @param {number} measured - measured offset (serverTime - clientTime), ms
 * @param {"http"|"socket"} [via]
 */
function applyMeasuredOffset(measured, via = "http") {
  if (hasSynced) {
    const delta = Math.abs(measured - drift);

    // Drift monitoring (TIME_DRIFT_TOLERANCE_MS): surface when the clock moved
    // more than the configured tolerance since the last sync.
    if (delta > driftToleranceMs) {
      console.warn(
        `[timeService] Clock drift ${Math.round(delta)}ms exceeds tolerance ${driftToleranceMs}ms (via ${via})`,
      );
    }

    // Outlier guard: a large jump must be confirmed by a second consecutive
    // sample before it is adopted. A genuine step change produces two agreeing
    // samples and is adopted on the second; a one-off outlier is dropped.
    if (delta > SUSPECT_JUMP_MS) {
      if (pendingOffset !== null && Math.abs(measured - pendingOffset) <= SUSPECT_JUMP_MS) {
        // Confirmed step — fall through and adopt.
        pendingOffset = null;
      } else {
        pendingOffset = measured;
        console.warn(
          `[timeService] Suspect offset jump of ${Math.round(delta)}ms held pending confirmation (via ${via})`,
        );
        // Keep existing drift; still mark sync as succeeded (we did reach the
        // server) and update bookkeeping timestamps.
        lastSyncAt = Date.now();
        syncFailed = false;
        return;
      }
    } else {
      pendingOffset = null;
    }
  }

  drift = measured;
  hasSynced = true;
  lastSyncAt = Date.now();
  syncFailed = false;
  monoAnchor = monoNow();
  wallAnchorAtSync = Date.now();
}

/**
 * Sync time with server via HTTP /api/time endpoint.
 * Used as fallback when socket is not connected.
 * @returns {Promise<number>} drift in milliseconds
 */
export async function syncTime() {
  try {
    const t0 = Date.now();
    const res = await axios.get(`${serverUrl}/time`);
    const t3 = Date.now();
    const data = res.data;

    const serverTime = new Date(data.utc).getTime();
    if (!Number.isFinite(serverTime)) {
      throw new Error("invalid server time payload");
    }

    const rtt = t3 - t0;
    lastRtt = rtt;

    if (typeof data.resyncIntervalMs === "number") {
      // Allow server to communicate drift tolerance via response if present.
      if (typeof data.driftToleranceMs === "number") {
        driftToleranceMs = data.driftToleranceMs;
      }
    }

    if (rtt > MAX_RTT_MS && hasSynced) {
      // Too slow to trust — keep last known good offset.
      console.warn(`[timeService] Discarding time sample with RTT ${rtt}ms (> ${MAX_RTT_MS}ms)`);
      lastSyncAt = Date.now();
      syncFailed = false;
      return drift;
    }

    // NTP-style midpoint: assume symmetric latency.
    const measured = serverTime - (t0 + t3) / 2;
    applyMeasuredOffset(measured, "http");

    return drift;
  } catch (error) {
    syncFailed = true;
    console.warn("[timeService] Time sync failed — using last known offset:", error.message);
    // Retain existing drift value (do not reset to 0)
    return drift;
  }
}

/**
 * Get the current server-adjusted time.
 * Uses a monotonic base so time advances smoothly and immune to wall-clock
 * jumps between resyncs.
 * @returns {Date}
 */
export function getCurrentTime() {
  if (!hasSynced) {
    return new Date(Date.now() + drift);
  }
  const elapsed = monoNow() - monoAnchor;
  return new Date(wallAnchorAtSync + drift + elapsed);
}

/**
 * Get the current drift value in milliseconds.
 * @returns {number}
 */
export function getDrift() {
  return drift;
}

/**
 * Set drift from external source (e.g., socket serverTime event).
 * Prefer setDriftFromSample when a client send/receive timestamp pair is
 * available, as it compensates for latency. This setter accepts a pre-computed
 * offset and routes it through the same outlier/monitoring guards.
 * @param {number} offsetMs
 */
export function setDrift(offsetMs) {
  if (!Number.isFinite(offsetMs)) return;
  applyMeasuredOffset(offsetMs, "socket");
}

/**
 * Compute and apply drift from a server timestamp and the client send/receive
 * timestamps (NTP-style midpoint). Use this for socket round-trips where t0/t3
 * are known.
 * @param {number} serverNow - server epoch ms
 * @param {number} t0 - client send time (Date.now) ms
 * @param {number} t3 - client receive time (Date.now) ms
 * @returns {number} applied/retained drift
 */
export function setDriftFromSample(serverNow, t0, t3) {
  if (!Number.isFinite(serverNow)) return drift;
  const rtt = t3 - t0;
  lastRtt = rtt;
  if (rtt > MAX_RTT_MS && hasSynced) {
    console.warn(
      `[timeService] Discarding socket time sample with RTT ${rtt}ms (> ${MAX_RTT_MS}ms)`,
    );
    return drift;
  }
  const measured = serverNow - (t0 + t3) / 2;
  applyMeasuredOffset(measured, "socket");
  return drift;
}

/**
 * Check if last sync attempt failed.
 * @returns {boolean}
 */
export function isSyncFailed() {
  return syncFailed;
}

/**
 * Get timestamp of last successful sync.
 * @returns {number}
 */
export function getLastSyncAt() {
  return lastSyncAt;
}

/**
 * Get the round-trip time (ms) of the last sync attempt.
 * @returns {number}
 */
export function getLastRtt() {
  return lastRtt;
}

/**
 * Reset internal state. Intended for tests.
 */
export function __resetForTest() {
  drift = 0;
  lastSyncAt = 0;
  syncFailed = false;
  lastRtt = Infinity;
  hasSynced = false;
  pendingOffset = null;
  driftToleranceMs = DEFAULT_DRIFT_TOLERANCE_MS;
  monoAnchor = monoNow();
  wallAnchorAtSync = Date.now();
}
